import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import { ChartDataSoundStatus } from '@app/core/enums/chart-data-sound-status.enum';
import {
  IAiPredictorDateChartData,
  IAiPredictorDateChartDataQueryParams,
  IAiPredictorDateChartDataValue,
} from '@app/api/interfaces/chart-data/date-ai-predictor';
import { LoggerDeploymentsDataMockService } from '@mock/data-services/logger-deployments-data-mock.service';
import { ILoggerDeploymentData } from '@app/api/interfaces/logger-deployment-data.interface';
import { generateDateDateChartData, sortByDate } from '@mock/utils/date-chart-data';
import { IAiPredictorDateChartDataRange } from '@app/api/interfaces/chart-data/date-ai-predictor';

@Injectable({ providedIn: 'root' })
export class LoggerDeploymentDateAiPredictorMockService extends MockService {
  private loggerDeploymentsDataMockService = inject(LoggerDeploymentsDataMockService);

  public get(
    projectId: string,
    deploymentId: string,
    filter: IAiPredictorDateChartDataQueryParams
  ): Observable<IApiResponse<IAiPredictorDateChartData, object>> {
    const deployment = this.loggerDeploymentsDataMockService.loggerDeploymentsData.find(
      el => el.deploymentId === deploymentId
    );

    const dataAll: IAiPredictorDateChartData = deployment
      ? this.generateAccordingToDeployment(deployment)
      : { ranges: [] };

    const data = sortByDate<IAiPredictorDateChartDataValue, IAiPredictorDateChartDataRange>(dataAll, filter);

    return this.mockResponse<IApiResponse<IAiPredictorDateChartData, object>>(
      'LOGGER_DEPLOYMENT_DATE-AI_PREDICTOR:get',
      {
        data,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateAccordingToDeployment(deployment: ILoggerDeploymentData): IAiPredictorDateChartData {
    return generateDateDateChartData<IAiPredictorDateChartDataValue, IAiPredictorDateChartDataRange>(
      new Date().toISOString(),
      (_, index: number) => ({
        deploymentId: deployment.deploymentId,
        serialNo: deployment.serialNo,
        warningThreshold: [61, 69][index % 1],
        criticalThreshold: [72, 80][index % 1],
        // this will be replaced!!!!
        collected: '',
        deployed: '',
      }),
      (date: string, index: number) => {
        let leakScore = convertStringInToSumOfAllCharacters(date + deployment.deploymentId + 'ai') % 100;

        const isExistExtraBigData = convertStringInToSumOfAllCharacters(deployment.deploymentId + 'ai') % 2 > 1;

        leakScore =
          leakScore < 10 && !isExistExtraBigData
            ? Math.round(leakScore / 2)
            : Math.round(leakScore * (leakScore % 5 || 1));
        leakScore = leakScore > 100 ? 100 : leakScore < 20 ? 0 : leakScore;

        const noSoundGroupSize = 7;
        const noSoundStartIndex = convertStringInToSumOfAllCharacters(deployment.deploymentId + 'ai') % 25;
        const status =
          index >= noSoundStartIndex && index < noSoundStartIndex + noSoundGroupSize
            ? ChartDataSoundStatus.NoSound
            : undefined;

        return {
          date,
          leakScore,
          status,
        };
      },
      [7, 11]
    );
  }
}
